import { BookletResumeComponent } from '@/components/booklet/booklet-resume/booklet-resume.component';
import { PropertyBookletBuildingCardComponent } from '@/components/booklet/property-booklet-building-card/property-booklet-building-card.component';
import { PropertyBookletCardComponent } from '@/components/booklet/property-booklet-card/property-booklet-card.component';
import {
  PropertyBookletCertificationCardComponent,
  PropertyBookletCertificationType
} from '@/components/booklet/property-booklet-certification-card/property-booklet-certification-card.component';
import { PropertyBookletCreateComponent } from '@/components/booklet/property-booklet-create/property-booklet-create.component';
import {
  PropertyBookletEquipmentCardComponent,
  PropertyBookletEquipmentType
} from '@/components/booklet/property-booklet-equipment-card/property-booklet-equipment-card.component';
import {
  PropertyBookletInsulationCardComponent,
  PropertyBookletInsulationType
} from '@/components/booklet/property-booklet-insulation-card/property-booklet-insulation-card.component';
import { ConfirmDialogComponent } from '@/components/confirm-dialog/confirm-dialog.component';
import { DataLoadingComponent } from '@/components/data-loading/data-loading.component';
import { DocAddWidgetComponent } from '@/components/doc-add-widget/doc-add-widget.component';
import { PropertyDocCardComponent } from '@/components/property-doc-card/property-doc-card.component';
import { SelectComponent } from '@/components/ui-forms/select/select.component';
import { DateFormatPipe } from '@/extensions/datePipe';
import { PropertyHelper } from '@/helpers/property.helper';
import { UserHelper } from '@/helpers/user.helper';
import { AlertService } from '@/services/alert.service';
import { DictionaryCacheService } from '@/services/dictionary-cache.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatStepperModule, StepperOrientation } from '@angular/material/stepper';
import {
  PropertiesBookletService,
  PropertyBooklet,
  PropertyBookletBuilding,
  PropertyBookletCertification,
  PropertyBookletEquipments,
  PropertyBookletInsulation,
  PropertyBookletInsulationDetail,
  PropertyBookletStatus,
  PropertyBookletType,
  RealProperty
} from '@eemo/api-client';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subscription, map } from 'rxjs';

export enum PropertyBookletBuildingType {
  housing = 'housing',
  surface = 'surface',
  waterNetwork = 'waterNetwork',
  electricityNetwork = 'electricityNetwork',
  gasNetwork = 'gasNetwork',
  ventilationNetwork = 'ventilationNetwork'
}

@Component({
  selector: 'app-property-booklet',
  templateUrl: './property-booklet.component.html',
  styleUrl: './property-booklet.component.scss',
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatStepperModule,
    MatProgressBarModule,
    DataLoadingComponent,
    DocAddWidgetComponent,
    SelectComponent,
    PropertyBookletCreateComponent,
    PropertyDocCardComponent,
    DateFormatPipe,
    TranslateModule,
    PropertyBookletInsulationCardComponent,
    PropertyBookletEquipmentCardComponent,
    PropertyBookletCardComponent,
    PropertyBookletBuildingCardComponent,
    BookletResumeComponent,
    PropertyBookletCertificationCardComponent,
    AsyncPipe
  ]
})
export class PropertyBookletComponent implements OnInit, OnDestroy {
  @Input() prop: RealProperty;

  private readonly propertiesBookletService = inject(PropertiesBookletService);

  loading = true;
  updating = false;
  deleting = false;
  subs: Subscription[] = [];

  hasInProgress = false;
  modeCreate = false;
  modeUpdate = false;
  bookletList: PropertyBooklet[];
  currentBooklet: PropertyBooklet;
  currentBookletValidated = false;
  currentBookletInsulation: PropertyBookletInsulation;
  currentBookletEquipment: PropertyBookletEquipments;
  currentBookletCertification: PropertyBookletCertification;
  canValidateBooklet = false;
  canUpdateProperty = false;
  isBuilding = false;
  subBuilding = new Subscription();
  subValidate = new Subscription();
  bookletInfo: PropertyBookletBuilding;

  propertyBookletBuildingType = PropertyBookletBuildingType;
  propertyBookletInsulationType = PropertyBookletInsulationType;
  propertyBookletEquipmentType = PropertyBookletEquipmentType;
  PropertyBookletCertificationType = PropertyBookletCertificationType;

  stepperOrientation: Observable<StepperOrientation>;

  constructor(
    private propHelper: PropertyHelper,
    private alertService: AlertService,
    private bookletService: PropertiesBookletService,
    private dialog: MatDialog,
    private dictCacheService: DictionaryCacheService,
    private userHelper: UserHelper
  ) {
    const breakpointObserver = inject(BreakpointObserver);

    this.stepperOrientation = breakpointObserver
      .observe('(min-width: 800px)')
      .pipe(map(({ matches }) => (matches ? 'horizontal' : 'vertical')));
  }

  ngOnInit(): void {
    this.canValidateBooklet = this.prop.certified;
    this.canUpdateProperty = this.userHelper.canUpdateProperty(this.prop);
    this.loadData();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  loadData() {
    this.loading = true;
    this.subs.push(
      this.bookletService.getRealPropertyBookletList(this.prop.uid).subscribe({
        next: (list) => {
          this.bookletList = list;
          if (list.length === 0) {
            this.modeCreate = true;
          }
          list.forEach((b) => {
            if (b.status == PropertyBookletStatus.IN_PROGRESS) {
              this.hasInProgress = true;
            }
          });
          this.loading = false;
        },
        error: () => (this.loading = false)
      })
    );
  }

  addBooklet() {
    this.currentBooklet = null;
    this.modeUpdate = false;
    this.modeCreate = true;
  }

  loadBooklet(booklet: PropertyBooklet) {
    this.currentBooklet = booklet;
    this.currentBookletValidated = booklet.status === PropertyBookletStatus.VALIDATED;
    this.isBuilding = this.currentBooklet?.type === PropertyBookletType.BUILDING;

    // building
    this.getBuildingData();

    this.subs.push(
      this.bookletService
        .getRealPropertyBookletInsulation(this.prop.uid, this.currentBooklet.uid)
        .subscribe((i) => (this.currentBookletInsulation = i))
    );

    this.subs.push(
      this.bookletService
        .getRealPropertyBookletEquipments(this.prop.uid, this.currentBooklet.uid)
        .subscribe((i) => (this.currentBookletEquipment = i))
    );

    this.subs.push(
      this.bookletService
        .getRealPropertyBookletCertification(this.prop.uid, this.currentBooklet.uid)
        .subscribe((i) => (this.currentBookletCertification = i))
    );
  }

  roofInsulationUpdated($event: PropertyBookletInsulationDetail) {
    if (this.currentBookletInsulation) {
      this.currentBookletInsulation.roof = $event;
    } else {
      this.currentBookletInsulation = { roof: $event };
    }
  }

  roofInsulationDeleted() {
    this.currentBookletInsulation.roof = undefined;
  }

  closeCurrentBooklet() {
    this.loadData();
    this.modeUpdate = false;
    this.currentBooklet = null;
  }

  updateCreatedBooklet(bookletId: string) {
    this.subs.push(
      this.bookletService.getRealPropertyBooklet(this.prop.uid, bookletId).subscribe((b) => {
        this.loadBooklet(b);
        this.modeCreate = false;
        this.modeUpdate = true;
      })
    );
  }

  confirmDeleteBooklet($event: PropertyBooklet) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: `Confirmez-vous la suppression de ce CIL ?`
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDeleteBooklet($event);
        }
      });
    }
  }

  doDeleteBooklet(booklet: PropertyBooklet) {
    this.deleting = true;
    this.subs.push(
      this.bookletService.deleteRealPropertyBooklet(this.prop.uid, booklet.uid).subscribe({
        next: () => {
          this.alertService.success('CIL supprimé');
          this.loadData();
          this.currentBooklet = null;
          this.deleting = false;
        },
        error: () => {
          this.deleting = false;
        }
      })
    );
  }

  // building
  getBuildingData() {
    this.subBuilding = this.propertiesBookletService
      .getRealPropertyBookletBuilding(this.prop.uid, this.currentBooklet.uid)
      .subscribe({
        next: (resp) => {
          this.bookletInfo = resp;
        }
      });
  }

  // insulation
  onUpdatedInsulation() {
    this.loadBooklet(this.currentBooklet);
  }
  onUpdatedBuilding() {
    this.loadBooklet(this.currentBooklet);
  }

  onUpdatedEquipment() {
    this.loadBooklet(this.currentBooklet);
  }

  onUpdatedCertification() {
    this.loadBooklet(this.currentBooklet);
  }

  validate() {
    //validation is allowed only for certified properties
    if (!this.canValidateBooklet) return;
    this.loading = true;
    this.subValidate = this.propertiesBookletService
      .validateRealPropertyBooklet(this.prop.uid, this.currentBooklet.uid)
      .subscribe({
        next: (resp) => {
          setTimeout(() => {
            this.loading = false;
            this.modeUpdate = false;
            this.modeCreate = false;
            this.loadData();
          }, 2000);
        },
        error: () => {
          this.loading = false;
        }
      });
  }
}
